import {Injectable} from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpClient, HttpContext } from '@angular/common/http';
import {catchError, finalize, map, Observable, shareReplay, switchMap, take, throwError} from 'rxjs';
import {environment} from "../../environments/environment";
import {TargetCompanyService} from "../_services/target-company.service";
import {GlobalStore} from "../global.store";
import {CSRF_TOKEN_RETRY, isCsrfTokenInvalidError} from "./csrf-token";


@Injectable()
export class HeadersInterceptor implements HttpInterceptor {
  private currentCompanyId: number | null = null;
  private csrfToken$: Observable<string> | null = null;

  constructor(
    private http: HttpClient,
    private targetCompanyService: TargetCompanyService,
    private globalStore: GlobalStore
  ) {
    this.globalStore.companyId$.subscribe((companyId) => {
      this.currentCompanyId = companyId;
    });
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.url.startsWith(environment.serverUrl)) {
      const skipCompanyScope = req.headers.has('X-Skip-Company-Id');
      const headers: Record<string, string> = {'X-Current-URL': window.location.href};
      const companyId = this.targetCompanyService.getRequestCompanyId(req.url)
        ?? this.targetCompanyService.getRequestCompanyId(window.location.href)
        ?? this.currentCompanyId;

      if (!skipCompanyScope && companyId && !req.headers.has('X-Company-Id')) {
        headers['X-Company-Id'] = String(companyId);
      }

      let modifiedReq = req.clone({
        setHeaders: headers
      });

      if (skipCompanyScope) {
        modifiedReq = modifiedReq.clone({
          headers: modifiedReq.headers.delete('X-Company-Id')
        });
      }

      if (environment.csrfEnabled && ['POST', 'DELETE', 'PUT'].includes(req.method)) {
        const changesAuthentication = /\/(login|logout)(\?|$)/.test(req.url);
        return this.handleWithCsrfToken(modifiedReq, next).pipe(
          // The backend issues a new CSRF token on login/logout
          finalize(() => {
            if (changesAuthentication) this.csrfToken$ = null;
          })
        );
      } else {
        return next.handle(modifiedReq);
      }
    } else {
      return next.handle(req);
    }
  }

  // The token stays valid until the backend rotates it (login/logout), so it is fetched once and reused
  // instead of an extra GET /csrf/token round-trip before every write.
  private handleWithCsrfToken(req: HttpRequest<any>, next: HttpHandler, isRetry = false): Observable<HttpEvent<any>> {
    return this.getCsrfToken().pipe(
      switchMap(token => next.handle(req.clone({setHeaders: {'X-XSRF-TOKEN': token}}))),
      catchError(err => {
        if (!isRetry && isCsrfTokenInvalidError(err)) {
          this.csrfToken$ = null;
          return this.handleWithCsrfToken(this.markAsCsrfRetry(req), next, true);
        }
        return throwError(() => err);
      })
    );
  }

  // A copy of the context: HttpRequest clones share the HttpContext object, so setting the flag in place would leak
  // it into the original request (e.g. one replayed later by OfflineQueueService)
  private markAsCsrfRetry(req: HttpRequest<any>): HttpRequest<any> {
    const context = new HttpContext();
    for (const token of req.context.keys()) {
      context.set(token, req.context.get(token));
    }
    return req.clone({context: context.set(CSRF_TOKEN_RETRY, true)});
  }

  private getCsrfToken(): Observable<string> {
    if (!this.csrfToken$) {
      const token$ = this.http
        .get<{ token: string }>(`${environment.serverUrl}/csrf/token`, {withCredentials: true})
        .pipe(
          map(result => result.token),
          catchError(err => {
            if (this.csrfToken$ === token$) this.csrfToken$ = null;
            return throwError(() => err);
          }),
          shareReplay(1)
        );
      this.csrfToken$ = token$;
    }
    return this.csrfToken$.pipe(take(1));
  }
}

